import { Tooltip, TooltipProps } from '@mui/material';

import { APP_ZOOM } from '@/styles/portalLayout';

import { styles } from './ZoomSafeTooltip.styles';

/** Test id of the shared counter-zoom container under <body>. */
export const ZOOM_SAFE_TOOLTIP_CONTAINER_TEST_ID = 'zoom-safe-tooltip-container';

/** The one shared counter-zoom node. Created on first use, shared by every tooltip. */
let tooltipContainer: HTMLDivElement | null = null;

/**
 * What: returns the shared counter-zoom node, creating it (under <body>) when it does not exist
 * or is no longer in the document.
 * Why: MUI calls this each time a tooltip opens. Recreating a disconnected node keeps tooltips
 * working after something removes it (for example a test cleanup that empties <body>).
 * Constraints: returns null on the server (no document); the app never renders there.
 */
const getTooltipContainer = (): HTMLDivElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  if (!tooltipContainer || !tooltipContainer.isConnected) {
    const node = document.createElement('div');
    node.setAttribute('data-testid', ZOOM_SAFE_TOOLTIP_CONTAINER_TEST_ID);
    // Absolute, 0x0 (no layout space) and counter-zoom. Set on the DOM node directly because
    // the node lives outside React. It is not an sx object, so it is not in the styles file.
    Object.assign(node.style, {
      position: 'absolute',
      width: '0px',
      height: '0px',
      zoom: String(1 / APP_ZOOM),
    });
    document.body.appendChild(node);
    tooltipContainer = node;
  }

  return tooltipContainer;
};

/**
 * Drop-in replacement for MUI `Tooltip` that lands on its anchor under the app-wide html zoom.
 *
 * What: takes the same props as MUI `Tooltip` and renders a MUI `Tooltip`.
 * Why: the app zooms <html> (APP_ZOOM, src/index.tsx). MUI Popper measures the anchor in
 * zoomed px but writes the position in CSS px, so a plain Tooltip portaled to <body> lands off
 * its anchor by about 15% of the anchor's distance from the page's top-left. Use this wrapper
 * instead of MUI `Tooltip` for every new tooltip.
 *
 * How (same mechanics as IconTooltipButton):
 * - The popper is portaled into one shared counter-zoom container under <body> (zoom
 *   1 / APP_ZOOM, absolute, 0x0, no layout space). <body> has no transform, so a fixed popper
 *   is placed against the viewport, also inside modals and drawers that use transforms.
 * - `popperOptions.strategy = 'fixed'` stops `overflow: hidden` parents from clipping it.
 * - The tooltip box re-applies `zoom: APP_ZOOM`, so size and text match the 85% UI. The
 *   theme's tooltip colors and typography are unchanged.
 *
 * Constraints:
 * - `arrow` is ignored. Popper writes the arrow offset in CSS px, which is wrong under zoom, so
 *   a caller's `arrow` is dropped on purpose. No arrow is ever shown.
 * - A caller's `PopperProps` are kept: `sx` is added after ours, `popperOptions` and `modifiers`
 *   are spread. We force only `container` and `popperOptions.strategy`.
 * - `title=""` renders no tooltip (MUI behavior), so callers can disable it by passing ''.
 * - Firefox does not support CSS `zoom`. The page is not zoomed there and the counter-zoom has
 *   no effect, so the tooltip is placed correctly there too (same as IconTooltipButton).
 * - Already-fixed tooltips (IconTooltipButton, TitleWithInfo, StatCard, ProfileSection,
 *   ChartTooltip) keep their own code and do not use this wrapper.
 * - Tooltips inside the shared @altirllc/shared `TooltipedOverflow` cannot use this wrapper
 *   (node_modules).
 */
export const ZoomSafeTooltip = ({ arrow: _arrow, PopperProps, ...rest }: TooltipProps) => {
  const callerSx = PopperProps?.sx;

  return (
    <Tooltip
      {...rest}
      PopperProps={{
        ...PopperProps,
        container: getTooltipContainer,
        popperOptions: { ...PopperProps?.popperOptions, strategy: 'fixed' },
        sx: [styles.tooltipPopper, ...(Array.isArray(callerSx) ? callerSx : [callerSx])],
      }}
    />
  );
};
